// src/components/dashboard/groups/GlobalRankingTab.tsx
// ✅ v2 — Ranking por groupScore (média do studyScore dos membros)

import React, { useState } from 'react';
import { Trophy, Users, Info, } from 'lucide-react';
import { GlobalRankingResponse } from '../GroupTypes';
import './GlobalRankingTab.css';
import GroupDetailsModal from './GroupDetailsModal';

interface GlobalRankingTabProps {
    rankingData: GlobalRankingResponse | null;
    isAdmin: boolean;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const GlobalRankingTab: React.FC<GlobalRankingTabProps> = ({
    rankingData,
    isAdmin,
    onShowToast
}) => {
    const [selectedGroup, setSelectedGroup] = useState<{
        isOpen: boolean;
        groupId: string;
        groupName: string;
        groupIcon?: string;
        description?: string;
        inviteCode: string;
        createdAt: Date | string;
        members: any[];
    } | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const handleViewDetails = async (groupId: string) => {
        setLoadingDetails(true);
        try {
            const token = localStorage.getItem('accessToken');

            const [groupRes, rankingRes] = await Promise.all([
                fetch(`/api/groups/${groupId}/details`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`/api/groups/${groupId}/ranking`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            if (!groupRes.ok || !rankingRes.ok) {
                onShowToast('Erro ao carregar detalhes do grupo', 'error');
                return;
            }

            const [groupData, rankingData] = await Promise.all([groupRes.json(), rankingRes.json()]);

            setSelectedGroup({
                isOpen: true,
                groupId: groupData._id,
                groupName: groupData.name,
                groupIcon: groupData.groupIcon,
                description: groupData.description,
                inviteCode: groupData.inviteCode,
                createdAt: groupData.createdAt,
                members: rankingData.ranking
            });
        } catch (error) {
            onShowToast('Erro ao carregar detalhes do grupo', 'error');
        } finally {
            setLoadingDetails(false);
        }
    };

    if (!rankingData || rankingData.ranking.length === 0) {
        return (
            <div className="global-ranking-empty">
                <Trophy size={48} className="empty-icon" />
                <h3>Nenhum grupo disponível</h3>
                <p>O ranking será exibido quando houver dados suficientes</p>
            </div>
        );
    }

    return (
        <div className="global-ranking-tab">
            <div className="global-header-wrapper">
                <div className="ranking-header">
                    <Trophy size={24} className="ranking-icon" />
                    <div className="ranking-title">
                        <h3>Ranking global de grupos</h3>
                        {isAdmin && (
                            <p className="ranking-subtitle">
                                {rankingData.totalGroups} grupos na plataforma
                            </p>
                        )}
                    </div>

                    {/* ✅ Badge explicativo da métrica */}
                    <div className="ranking-metric-badge">
                        <span>Ordenado por Pontuação Geral média do grupo</span>
                    </div>
                </div>

                <div className="ranking-list">
                    {rankingData.ranking.map((group, index) => {
                        const isTop3 = index < 3;

                        return (
                            <div
                                key={group.groupId}
                                className={`ranking-item ${isTop3 ? 'top' : ''} rank-${index + 1}`}
                            >
                                <div className="ranking-item-top-row">
                                    {/* Posição */}
                                    <div className="rank-position">
                                        <span className="rank-number">{index + 1}</span>
                                    </div>

                                    {/* Ícone */}
                                    {group.groupIcon ? (
                                        <div className="group-icon-wrapper">
                                            <img
                                                src={group.groupIcon || ''}
                                                alt={group.groupName}
                                                className="group-icon"
                                            />
                                        </div>
                                    ) : (
                                        <div className="group-icon-placeholder">
                                            <Users size={20} />
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="rank-info">
                                        <h4 className="group-name">{group.groupName}</h4>
                                        <div className="rank-stats">
                                            <span className="stat">
                                                <Users size={14} />
                                                {group.memberCount} {group.memberCount === 1 ? 'membro' : 'membros'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Score */}
                                    <div className="rank-score">
                                        <div className="score-label">Pontuação</div>
                                        <div className="score-value">{group.groupScore ?? group.averageMastery}</div>
                                        <div className="score-unit">pts / membro</div>
                                    </div>
                                </div>

                                {isAdmin && (
                                    <button
                                        className="btn-view-ranking"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewDetails(group.groupId);
                                        }}
                                        disabled={loadingDetails}
                                    >
                                        {loadingDetails ? 'Carregando' : 'Ver detalhes'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!isAdmin && rankingData.totalGroups > 10 && (
                    <div className="ranking-footer">
                        <Info size={16} />
                        <p>
                            Mostrando os 10 grupos com maior pontuação média.
                            Total na plataforma: <strong>{rankingData.totalGroups}</strong>
                        </p>
                    </div>
                )}
            </div>

            {selectedGroup && (
                <GroupDetailsModal
                    isOpen={selectedGroup.isOpen}
                    groupId={selectedGroup.groupId}
                    groupName={selectedGroup.groupName}
                    groupIcon={selectedGroup.groupIcon}
                    description={selectedGroup.description}
                    inviteCode={selectedGroup.inviteCode}
                    createdAt={selectedGroup.createdAt}
                    members={selectedGroup.members}
                    onClose={() => setSelectedGroup(null)}
                />
            )}
        </div>
    );
};

export default GlobalRankingTab;